// Resolves the minimum payment amount that applies to the plan the agent is building, from the
// BE-supplied `min_payment_floor_by_method` map on /payment_plan/info. ACH and debit floors are
// portfolio configuration; personal check is a flat BE constant.
//
// Extracted as a pure function so it can be unit-tested without React + Redux, matching
// paymentPlanMinPaymentLookup.js. The wizard hook is the only caller.

const ACH_CODE_KEY = 'payment_method_ach'
const DEBIT_CODE_KEY = 'payment_method_debit_card'

/**
 * @param {object} args
 * @param {object} args.floorByMethod — BE-supplied map keyed method_code_key → float
 * @param {number|undefined} args.methodId — DDO id of the selected payment method, if any
 * @param {Array<{ id: number, code_key: string }|undefined>} args.dropdownOptions
 * @returns {number|null} the applicable per-payment minimum, or null when the BE floors
 *   haven't loaded (the caller falls back to DEFAULT_MIN_INSTALLMENT_AMOUNT)
 */
function resolveApplicableMinimum({ floorByMethod, methodId, dropdownOptions }) {
  const floors = floorByMethod || {}

  const selected = selectedMethodFloor({ floors, methodId, dropdownOptions })
  if (selected !== null) return selected

  // No method picked yet: the higher of ACH and debit keeps the figure valid whichever
  // of the two the agent goes on to choose.
  const candidates = [floors[ACH_CODE_KEY], floors[DEBIT_CODE_KEY]].filter((v) => typeof v === 'number')
  return candidates.length ? Math.max(...candidates) : null
}

/**
 * The most permissive floor still open to the agent — for gates that BLOCK the plan, as opposed
 * to the figure displayed.
 *
 * Blocking on resolveApplicableMinimum would foreclose a method that still works: with ACH and
 * debit at $25 and personal check at $5, an $18 balance is fundable by check, so refusing it
 * before the agent has chosen is wrong. Once a method is chosen, that method's floor is the
 * answer for both.
 *
 * @param {object} args
 * @param {object} args.floorByMethod — BE-supplied map keyed method_code_key → float
 * @param {number|undefined} args.methodId — DDO id of the selected payment method, if any
 * @param {Array<{ id: number, code_key: string }|undefined>} args.dropdownOptions
 * @param {Array<number>|undefined} args.availableMethodIds — DDO ids the wizard actually offers
 *   for this loan. Narrowing to these matters: a method the loan cannot use must not license a
 *   balance no usable method could fund.
 * @returns {number|null} the lowest usable minimum, or null when nothing is resolvable yet
 */
function resolveLowestAvailableMinimum({ floorByMethod, methodId, dropdownOptions, availableMethodIds }) {
  const floors = floorByMethod || {}

  const selected = selectedMethodFloor({ floors, methodId, dropdownOptions })
  if (selected !== null) return selected

  const offered = (availableMethodIds || [])
    .map((id) => floors[dropdownOptions?.find((o) => o?.id === id)?.code_key])
    .filter((v) => typeof v === 'number')
  return offered.length ? Math.min(...offered) : null
}

function selectedMethodFloor({ floors, methodId, dropdownOptions }) {
  if (!methodId) return null
  const methodCodeKey = dropdownOptions?.find((o) => o?.id === methodId)?.code_key
  const methodFloor = floors[methodCodeKey]
  return typeof methodFloor === 'number' ? methodFloor : null
}

module.exports = { resolveApplicableMinimum, resolveLowestAvailableMinimum }
